import { Column, Loader, Row, Typography } from '@/components'
import { ThemeColor } from '@/enums'
import { ButtonState } from '@/modules/checkout/components/CheckoutBase'
import {
  KillerQuestionsList,
  NumberedListCounter,
} from '@/pages/CheckoutPage/styles'
import {
  usePublicCheckoutKillerQuestions,
  usePublicKillerQuestionsKeys,
  useUpdateCheckoutKillerQuestions,
} from '@/services'
import { noop, snakeToCamelCase } from '@/utils'
import { AdaptiveTooltip } from '@surein/ui'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

interface KillerQuestionsLabels {
  [key: string]: string
}
interface PublicKillerQuestionsStepProps {
  openContact: () => void
  onNext: () => void
  setButtonState: (state: ButtonState) => void
  setSecondaryButtonState: (state: ButtonState) => void
}

export const PublicKillerQuestionsStep = ({
  openContact,
  onNext,
  setButtonState,
  setSecondaryButtonState,
}: PublicKillerQuestionsStepProps) => {
  const { t } = useTranslation()
  const { categoryId } = useParams<{ categoryId: string }>()
  const killerQuestionsKeysResponse = usePublicKillerQuestionsKeys(
    categoryId || '',
  )
  const { data, isLoading } = usePublicCheckoutKillerQuestions()
  const killerQuestionLabels = t('recommendations.killerQuestions', {
    returnObjects: true,
  }) as KillerQuestionsLabels

  const saveKillerQuestionsMutation = useUpdateCheckoutKillerQuestions()

  const makeAnswers = useCallback(
    (isAccepted: boolean) => {
      const answerRecord: Record<string, boolean> = {}

      Object.keys(data ?? {}).forEach((key) => {
        answerRecord[key] = isAccepted
      })

      return answerRecord
    },
    [data],
  )

  const handleError = useCallback(() => {
    openContact()
  }, [openContact])

  const handleNext = useCallback(() => {
    onNext()
  }, [onNext])

  const handleSubmit = useCallback(
    async (answers: Record<string, boolean>) => {
      try {
        await saveKillerQuestionsMutation.mutateAsync(answers)
        handleNext()
      } catch (_error) {
        handleError()
      }
    },
    [handleError, handleNext, saveKillerQuestionsMutation],
  )

  useEffect(() => {
    setButtonState({
      onClick: () => handleSubmit(makeAnswers(true)),
      text: t('iConfirm'),
      loading: saveKillerQuestionsMutation.isLoading,
    })
    setSecondaryButtonState({
      onClick: () => handleSubmit(makeAnswers(false)),
      text: t('iDoNotConfirm'),
    })

    return () => {
      setButtonState({
        onClick: noop,
        text: t('next'),
        loading: false,
      })
      setSecondaryButtonState({
        onClick: noop,
        text: t('iDoNotConfirm'),
      })
    }
  }, [
    handleSubmit,
    makeAnswers,
    saveKillerQuestionsMutation.isLoading,
    t,
    setButtonState,
    setSecondaryButtonState,
  ])

  if (isLoading || killerQuestionsKeysResponse.isLoading) return <Loader />

  return (
    <Column gap="24px">
      <KillerQuestionsList>
        {killerQuestionsKeysResponse?.data?.map(
          (question: string, index: number) => (
            <li key={question}>
              <Row gap="16px" alignItems="center">
                <NumberedListCounter>{index + 1}</NumberedListCounter>
                <Typography color={ThemeColor.b50} variant="p1Body">
                  {killerQuestionLabels[snakeToCamelCase(question)]}
                </Typography>
              </Row>
            </li>
          ),
        )}
      </KillerQuestionsList>
      <Row justifyContent="flex-end">
        <AdaptiveTooltip content={t('whyDoWeAskTooltip')}>
          <Typography variant="footnote" color={ThemeColor.info1}>
            {t('whyDoWeAsk')}
          </Typography>
        </AdaptiveTooltip>
      </Row>
    </Column>
  )
}
